import type { SolutionSurfaceTone } from './solutionSurfaceTone';

type FaqToneClasses = {
  itemBorderBottom: string;
  itemBorderTop: string;
  questionText: string;
  answerText: string;
  iconText: string;
};

/*
 * Only the color utilities differ between tones; layout, typography, and
 * interaction utilities are shared below so the two tones can never drift in
 * spacing. The dark values are the ones the Industrial page shipped with.
 */
const TONE_CLASSES: Record<SolutionSurfaceTone, FaqToneClasses> = {
  dark: {
    itemBorderBottom: 'border-b-[rgba(255,255,255,0.14)]',
    itemBorderTop: 'first:border-t-[rgba(255,255,255,0.14)]',
    questionText: 'text-apps-brandGrayWhite',
    answerText: 'text-apps-brandGray700',
    iconText: 'text-apps-brandGray600',
  },
  light: {
    itemBorderBottom: 'border-b-apps-coreProductDivider',
    itemBorderTop: 'first:border-t-apps-coreProductDivider',
    questionText: 'text-apps-brandGrayBlack',
    answerText: 'text-apps-brandGray200',
    iconText: 'text-apps-brandGray400',
  },
};

/**
 * ### SolutionFaqClasses
 *
 * Resolved class strings for one FAQ disclosure row, ready to place on the
 * row wrapper, its question button, the answer region, and the indicator icon
 * respectively.
 */
export type SolutionFaqClasses = {
  /**
   * ### item
   *
   * Applied to the row wrapper. Carries the `group` marker the icon and the
   * answer region rely on to follow the row's expanded state.
   */
  item: string;

  /**
   * ### question
   *
   * Applied to the question button; lays the heading and icon out as a
   * two-column grid across the full row width.
   */
  question: string;

  /**
   * ### answerFrame
   *
   * Applied to the answer region. Holds the open and closed heights the row
   * animates between, driven by the `data-expanded` marker the row sets on
   * the wrapper, and drops the motion for a row the browser reveals from
   * find-in-page, which is marked `data-instant` for that one frame.
   */
  answerFrame: string;

  /**
   * ### answerClip
   *
   * Applied to the single child of the frame. Clipping it is what lets the
   * frame shrink below the answer's own height; without it the grid row keeps
   * the paragraph's automatic minimum size and nothing moves.
   */
  answerClip: string;

  /**
   * ### answer
   *
   * Applied to the answer paragraph inside the region.
   */
  answer: string;

  /**
   * ### icon
   *
   * Applied to the plus/minus indicator svg.
   */
  icon: string;
};

/**
 * ### faqSectionClasses
 *
 * Resolves the class strings of a FAQ disclosure row for a surface tone.
 * Colors come from the tone; every other utility is identical across tones.
 *
 * @param tone Surface tone the row is rendered on
 * @returns Class strings for the row, its question button, answer, and icon
 */
export function faqSectionClasses(
  tone: SolutionSurfaceTone,
): SolutionFaqClasses {
  const colors = TONE_CLASSES[tone];

  return {
    item: `group border-b ${colors.itemBorderBottom} first:border-t ${colors.itemBorderTop}`,
    question:
      'grid w-full cursor-pointer grid-cols-[1fr_24px] items-center gap-6 py-5 text-left ' +
      `text-[16px] font-bold leading-[150%] tracking-[-0.32px] ${colors.questionText} ` +
      'focus-visible:outline-2 focus-visible:outline-apps-corePrimary01 focus-visible:outline-offset-4 ' +
      'lg:grid-cols-[1fr_36px] lg:py-6 lg:text-[clamp(18px,1.667vw,20px)]',
    answerFrame:
      'grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out ' +
      'group-data-[expanded]:grid-rows-[1fr] group-data-[instant]:transition-none ' +
      'motion-reduce:transition-none',
    answerClip: 'overflow-hidden',
    answer:
      'pb-5 pr-0 text-[15px] font-medium leading-[170%] tracking-[-0.3px] ' +
      `${colors.answerText} lg:pb-6 lg:pr-[60px] lg:text-[17px]`,
    icon: `justify-self-end ${colors.iconText} lg:h-[36px] lg:w-[36px]`,
  };
}
